import { AxiosInstance } from 'axios'
import {
  GroupIntegration,
  MergeCommonModel,
  MergeFilterBasePayload,
  MergeFilterSinglePayload,
  MergeIntegration,
  MergeIntegrationDownloadPreviewMembersPayload,
  MergeIntegrationPreviewMembersPayload,
  MergeIntegrationsFilterPayload,
  MergeLinkTokenResponse,
  MergePreviewedMember,
  MergePreviewManagersPayload,
  MergePreviewManagersResponse,
  MergeSaveIntegrationImportStrategyPayload,
  RemoteFieldResponse
} from 'types/integrations'
import { INTEGRATIONS_ENDPOINTS } from 'api/ApiRoutes'
import { ID, PaginatedResult } from 'types/utility'

class IntegrationsApi {
  constructor(private axiosInstance: AxiosInstance) {}

  getMergeAvailableIntegrations = async () => {
    const { data } = await this.axiosInstance.get<MergeIntegration[]>(
      INTEGRATIONS_ENDPOINTS.MERGE_INTEGRATIONS_METADATA
    )
    return data
  }

  getMergeLinkToken = async (teamId: ID, tokenId?: ID) => {
    const d = await this.axiosInstance.post<MergeLinkTokenResponse>(
      INTEGRATIONS_ENDPOINTS.MERGE_LINK_TOKEN(teamId),
      tokenId ? { group_integration_id: tokenId } : undefined
    )
    return d.data
  }

  relinkMergeLinkToken = async (teamId: ID, groupIntegrationId: ID) => {
    const d = await this.axiosInstance.post<MergeLinkTokenResponse>(
      INTEGRATIONS_ENDPOINTS.MERGE_RELINK_TOKEN(teamId, groupIntegrationId)
    )
    return d.data
  }

  previewManagers = async ({
    teamId,
    groupIntegrationId,
    merge_work_email,
    team_assignment_strategy,
    remote_field_traversal_path,
    remote_url_path
  }: MergePreviewManagersPayload) => {
    const urlQueries = new URLSearchParams()
    urlQueries.append('merge_work_email', merge_work_email)
    if (team_assignment_strategy) {
      urlQueries.append('team_assignment_strategy', team_assignment_strategy)
    }

    if (remote_field_traversal_path) {
      remote_field_traversal_path.forEach((item) => {
        urlQueries.append(`remote_field_traversal_path[]`, item)
      })
    }

    if (remote_url_path) {
      urlQueries.append('remote_url_path', remote_url_path)
    }

    const d = await this.axiosInstance.get<MergePreviewManagersResponse>(
      `${INTEGRATIONS_ENDPOINTS.MERGE_PREVIEW_MANAGERS(teamId, groupIntegrationId)}?${urlQueries}`
    )
    return d.data
  }

  downloadManagers = async ({
    teamId,
    groupIntegrationId,
    team_assignment_strategy,
    remote_field_traversal_path,
    remote_url_path
  }: Omit<MergePreviewManagersPayload, 'merge_work_email'>) => {
    const urlQueries = new URLSearchParams()
    if (team_assignment_strategy) {
      urlQueries.append('team_assignment_strategy', team_assignment_strategy)
    }

    if (remote_field_traversal_path) {
      remote_field_traversal_path.forEach((item) => {
        urlQueries.append(`remote_field_traversal_path[]`, item)
      })
    }

    if (remote_url_path) {
      urlQueries.append('remote_url_path', remote_url_path)
    }

    const d = await this.axiosInstance.get<{
      token_id: ID
    }>(
      `${INTEGRATIONS_ENDPOINTS.MERGE_DOWNLOAD_MANAGERS(teamId, groupIntegrationId)}?${urlQueries}`
    )
    return d.data
  }

  swapMergeTemporalToken = async ({
    publicToken,
    groupIntegrationId,
    teamId
  }: {
    publicToken: string
    groupIntegrationId: ID
    teamId: ID
  }) => {
    const d = await this.axiosInstance.post<GroupIntegration>(
      INTEGRATIONS_ENDPOINTS.MERGE_SWAP(teamId),
      {
        group_integration: {
          public_token: publicToken,
          group_integration_id: groupIntegrationId
        }
      }
    )
    return d.data
  }

  getMergeGroupIntegration = async (teamId: ID, id: ID) => {
    const d = await this.axiosInstance.get<GroupIntegration>(
      INTEGRATIONS_ENDPOINTS.MERGE_INTEGRATION(teamId, id)
    )
    return d.data
  }

  getMergeGroupIntegrations = async (teamId: ID, pageSize = 100) => {
    const d = await this.axiosInstance.get<PaginatedResult<GroupIntegration>>(
      INTEGRATIONS_ENDPOINTS.MERGE_INTEGRATIONS(teamId),
      {
        params: {
          page_size: pageSize
        }
      }
    )
    return d.data
  }

  unlinkMergeIntegration = async (teamId: ID, id: ID) => {
    const d = await this.axiosInstance.delete<{
      group_integration_id: string | null
    }>(INTEGRATIONS_ENDPOINTS.MERGE_INTEGRATION(teamId, id))
    return d.data
  }

  getIntegrationsFilters = async ({
    teamId,
    integrationId
  }: {
    teamId: ID
    integrationId: ID
  }) => {
    const d = await this.axiosInstance.get<{
      common_models: MergeCommonModel[]
    }>(
      INTEGRATIONS_ENDPOINTS.MERGE_INTEGRATION_DROPDOWN_FILTERS(
        teamId,
        integrationId
      )
    )
    return d.data
  }

  getIntegrationsFilterDetails = async ({
    teamId,
    integrationId,
    path,
    cursor,
    page_size = 100,
    q
  }: MergeFilterBasePayload) => {
    const params = new URLSearchParams()
    if (cursor) {
      params.append('cursor', cursor)
    }
    params.append('page_size', page_size.toString())
    if (q) {
      params.append('q', q)
    }
    const d = await this.axiosInstance.get<
      PaginatedResult<MergePreviewedMember>
    >(
      `${INTEGRATIONS_ENDPOINTS.MERGE_INTEGRATION(teamId, integrationId)}${path}${path.includes('?') ? '&' : '?'}${params.toString()}`
    )
    return d.data
  }

  getIntegrationsFilterSingle = async ({
    teamId,
    integrationId,
    path,
    id
  }: MergeFilterSinglePayload) => {
    const cleanPath = path.includes('?')
      ? path.substring(0, path.indexOf('?'))
      : path
    const d = await this.axiosInstance.get<MergePreviewedMember | null>(
      `${INTEGRATIONS_ENDPOINTS.MERGE_INTEGRATION(teamId, integrationId)}${cleanPath}/${id}`
    )
    return d.data
  }

  getIntegrationsFiltersAndConditions = async ({
    teamId,
    integrationId
  }: {
    teamId: ID
    integrationId: ID
  }) => {
    const d = await this.axiosInstance.get<MergeIntegrationsFilterPayload>(
      INTEGRATIONS_ENDPOINTS.MERGE_INTEGRATION_FILTERS(teamId, integrationId)
    )
    return d.data
  }

  saveIntegrationImportStrategy = async ({
    teamId,
    groupIntegrationId,
    sync_existing_members,
    ...payload
  }: MergeSaveIntegrationImportStrategyPayload) => {
    const d = await this.axiosInstance.put<GroupIntegration>(
      INTEGRATIONS_ENDPOINTS.MERGE_INTEGRATION(teamId, groupIntegrationId),
      {
        group_integration: {
          ...payload,
          sync_existing_members: sync_existing_members ?? undefined
        }
      }
    )
    return d.data
  }

  saveIntegrationsFiltersAndConditions = async ({
    teamId,
    integrationId,
    payload
  }: {
    teamId: ID
    integrationId: ID
    payload: MergeIntegrationsFilterPayload
  }) => {
    const d = await this.axiosInstance.post<MergeIntegrationsFilterPayload>(
      INTEGRATIONS_ENDPOINTS.MERGE_INTEGRATION_FILTERS(teamId, integrationId),
      {
        group_integration: payload
      }
    )
    return d.data
  }

  getPreviewIntegrationMembers = async ({
    teamId,
    integrationId,
    ...rest
  }: MergeIntegrationPreviewMembersPayload) => {
    const d = await this.axiosInstance.post<
      PaginatedResult<MergePreviewedMember>
    >(
      INTEGRATIONS_ENDPOINTS.MERGE_INTEGRATION_MEMBERS(teamId, integrationId),
      rest
    )
    return d.data
  }

  getDownloadPreviewIntegrationMembers = ({
    teamId,
    integrationId,
    payload
  }: {
    teamId: ID
    integrationId: ID
    payload: MergeIntegrationDownloadPreviewMembersPayload
  }) => {
    return this.axiosInstance
      .post<{
        result: string
        job_token_id: ID
      }>(
        INTEGRATIONS_ENDPOINTS.MERGE_INTEGRATION_PREVIEW_MEMBERS(
          teamId,
          integrationId
        ),
        payload
      )
      .then((d) => d.data)
  }

  getIntegrationRemoteFields = async ({
    integrationId,
    teamId
  }: {
    integrationId: string
    teamId: string
  }) => {
    const { data } = await this.axiosInstance.get<RemoteFieldResponse>(
      INTEGRATIONS_ENDPOINTS.MERGE_INTEGRATION_REMOTE_FIELDS(
        teamId,
        integrationId
      )
    )
    const fieldValueToOmit = '<redacted>'

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    function removeRedacted(obj: any) {
      if (Array.isArray(obj)) {
        for (let i = obj.length - 1; i >= 0; i--) {
          if (
            obj[i] === fieldValueToOmit ||
            (typeof obj[i] === 'object' &&
              Object.values(obj[i]).includes(fieldValueToOmit))
          ) {
            obj.splice(i, 1)
          }
        }
      } else if (obj !== null && typeof obj === 'object') {
        Object.keys(obj).forEach((key) => {
          obj[key] = removeRedacted(obj[key])
          if (obj[key] === fieldValueToOmit || obj[key] === undefined) {
            delete obj[key]
          }
        })
      }
      return obj === fieldValueToOmit ? undefined : obj
    }

    removeRedacted(data.tree)
    return data
  }
}

export default IntegrationsApi
